import { useMemo, useRef } from 'react';

import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';

import { queryParameters } from '~constants';
import { NfcTag, SkcKeysService } from '~features/nfc';
import { IdReference } from '~interfaces';

const useUrlSearchParamsSkcUsersAndNfcTags = (): [
	NfcTag | IdReference | null,
	(value: NfcTag | null) => void,
] => {
	const [searchParams, setSearchParams] = useSearchParams();

	const service = useRef(new SkcKeysService());

	const nfcTagId = searchParams.get(queryParameters.Key);

	const { data } = useSWR(
		nfcTagId != null ? [service.current.basePath, nfcTagId] : null,
		([_, args]) => service.current.getKeyId(args),
		{
			// We will use this hook over multiple components. This prevent this from overfetching
			// but try to keep it up to date every once in a while. The organisation may change very
			// rarely
			dedupingInterval: 10000, // ms
		}
	);

	const nfcTag = useMemo<NfcTag | IdReference | null>(() => {
		if (!nfcTagId) {
			return null;
		}

		return data ?? (nfcTagId != null ? { id: nfcTagId } : null);
	}, [nfcTagId, data]);

	const setNfcTag = (value: NfcTag | null) => {
		if (value == null) {
			searchParams.delete(queryParameters.Key);
		} else {
			searchParams.set(queryParameters.Key, value.skcId ?? value.id);
		}

		setSearchParams(searchParams);
	};

	return [nfcTag, setNfcTag];
};

export default useUrlSearchParamsSkcUsersAndNfcTags;
